<template>
  <div class="profile-list">
    <div class="profile-list__header">
      <div>
        <s-search-input
          class="profile-list__search-input"
          v-model="searchTerm"
          v-s-trigger="changingSearch"
          :placeholder="$t('profile-manager.query.search-placeholder')"
        />
      </div>
      <div>
        <b-button
          @click="triggerGoToTree()"
          variant="outline-primary"
        >
          {{ $t('profile-manager.query.tree-view') }}
        </b-button>
        <b-button
          @click="newProfile()"
          variant="primary"
        >
          {{ $t('profile-manager.query.new-profile') }}
        </b-button>
      </div>
    </div>
    <div>
      <s-table
        :fields="fields"
        :items="items"
        :busy="loading"
        class="profile-list__table"
        :sort-by="sortBy.field"
        :sort-desc="sortBy.desc"
        @sort-changed="sort($event)"
      >
        <template #cell(linkType)="data">
          <b-badge
            v-for="linkType in filterLinkTypes(data.item.linkType)" :key="linkType"
            pill :class="'profile-list__link-badge_' + getLinkTypeMod(linkType)">
            <span :class="'fa fa-' + getIconForLink(linkType)"/>
            {{ $t('profile-manager.query.profile-link.' + getLinkTypeMod(linkType) ) }}
          </b-badge>
        </template>
        <template #cell(description)="data">
          <truncated-description-field
            :value="data.value"
            :unique-id="getUniqueId()"
          />
        </template>
        <template #cell(status)="data">
          <span :class="getStatusClass(data.item)">
            {{ $t('profile-manager.query.status.' + getStatusMod(data.item) ) }}
          </span>
        </template>
        <template #cell(description)="{ value }">
          {{ value || '-' }}
        </template>
        <template #cell(actions)="data">
          <b-button
            :disabled="!data.item.canEdit"
            @click="editProfile(data.item)"
            variant="outline-primary"
            size="sm"
          >
            <span class="fa fa-edit" />
            {{ $t('profile-manager.query.edit-profile') }}
          </b-button>
          <b-button
            :disabled="!data.item.canRemove"
            @click="deleteProfile(data.item)"
            variant="primary"
            size="sm"
          >
            <span class="far fa-trash-alt" />
            {{ $t('profile-manager.query.delete-profile') }}
          </b-button>
          <b-dropdown variant="link" offset="-15" right no-caret :disabled="!data.item.canEdit">
            <template slot="button-content">
              <i
                aria-hidden="true"
                class="fas fa-ellipsis-v text-gray"
              />
            </template>
            <b-dropdown-item @click="blockActivateProfile(data.item)">
              {{$t('profile-manager.query.options.' + getBlockActivateTranslatePath(data.item))}}
            </b-dropdown-item>
            <b-dropdown-item @click="showLinkedUsers(data.item)">
              {{$t('profile-manager.query.options.show-linked-users')}}
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </s-table>
    </div>
    <grid-paginator
      :total-rows="totalItems"
      :per-page="perPage"
      v-model="currentPage"
      @input="paging()"
    />
  </div>
</template>

<script>
import GridPaginator from '@/components/lists/Paginator';
import { delayed } from '@/services/observables';
import Service from '@/domain/services/generalPanel/adminPanel/profile/profileService';
import { getUniqueId } from '@/services/createId';
import TruncatedDescriptionField from '@/components/TruncatedDescriptionField';
import { RegisterStatus, LinkType } from 'sigma-mappings';

export default {
  components: {
    GridPaginator,
    TruncatedDescriptionField,
  },
  props: {},
  data: vm => ({
    doDelayedRequest: delayed((...args) => vm.doRequest(...args)),
    searchTerm: null,
    currentPage: 1,
    totalItems: null,
    loading: true,
    perPage: 10,
    items: [],
    sortBy: {
      field: 'name',
      desc: false,
    },
    fields: [
      {
        key: 'linkType',
        sortable: false,
        label: '',
        class: 'profile-list__cell-link',
      },
      {
        key: 'name',
        sortable: true,
        label: vm.$t('profile-manager.query.column-titles.profile'),
        class: 'profile-list__cell-name',
      },
      {
        key: 'description',
        sortable: true,
        label: vm.$t('profile-manager.query.column-titles.description'),
        class: 'truncate profile-list__cell-description',
      },
      {
        key: 'status',
        sortable: true,
        label: vm.$t('profile-manager.query.column-titles.status'),
        class: 'profile-list__cell-status',
      },
      {
        key: 'actions',
        sortable: false,
        label: '',
        class: 'profile-list__cell-actions',
      },
    ],
  }),
  created() {
    this.refreshItems();
  },
  methods: {
    getUniqueId,
    sort(evt) {
      this.sortBy.field = evt.sortBy;
      this.sortBy.desc = evt.sortDesc;
      this.currentPage = 1;
      this.refreshItems();
      return false;
    },
    paging() {
      this.refreshItems();
    },
    changingSearch() {
      if (this.searchTerm && this.searchTerm.length < 3) return;
      this.currentPage = 1;
      this.refreshItems(true);
    },
    getBlockActivateTranslatePath(profile) {
      switch (profile.status) {
        case RegisterStatus.BLOCKED:
          return 'activate';
        case RegisterStatus.ACTIVE:
          return 'block';
        default:
          throw new Error(`Unknown status: "${profile.status}".`);
      }
    },
    getStatusMod(profile) {
      switch (profile.status) {
        case RegisterStatus.BLOCKED:
          return 'blocked';
        case RegisterStatus.ACTIVE:
          return 'active';
        default:
          throw new Error(`Unknown status: "${profile.status}".`);
      }
    },
    getStatusClass(profile) {
      switch (profile.status) {
        case RegisterStatus.BLOCKED:
          return 'text-red';
        case RegisterStatus.ACTIVE:
          return 'text-green';
        default:
          throw new Error(`Unknown status: "${profile.status}".`);
      }
    },
    triggerGoToTree() {
      this.$emit('go-to-tree');
    },
    newProfile() {
      this.$router.push({
        name: 'profile.new',
      });
    },
    editProfile(profile) {
      this.$router.push({
        name: 'profile.edit',
        params: {
          profileId: profile.id,
        },
      });
    },
    deleteProfile(profile) {
      this.$emit('delete-profiles', { ids: [profile.id], callback: this.refreshItems });
    },
    showLinkedUsers(profile, filter = false) {
      this.$emit('show-linked-users', {
        profilesIds: [profile.id],
        filterOnlySelectedProfile: filter,
      });
    },
    blockProfile(profile) {
      Service.blockProfile(
        profile.id,
      usersWithUniqueProfile => this.confirmBlockProfile(
          profile,
          usersWithUniqueProfile,
        ).toPromise(),
      ).then(() => {
        this.$pushAlert({ code: 'MS008' });
        this.refreshItems();
      });
    },
    unblockProfile(profile) {
      Service.unblockProfile(profile.id).then(() => {
        this.$pushAlert({ code: 'MS009' });
        this.refreshItems();
      });
    },
    blockActivateProfile(profile) {
      if (profile.status === RegisterStatus.ACTIVE) {
        this.blockProfile(profile);
        return;
      }
      if (profile.status === RegisterStatus.BLOCKED) {
        this.$confirmMessage({ code: 'MC014' })
          .accepted(() => {
            this.unblockProfile(profile);
          });
      }
    },
    confirmBlockProfile(profile, response) {
      if (response.usersWithUniqueProfile > 0) {
        return this.$confirmMessage({
          code: 'MC012',
          messageParams: {
            Users: (response.usernamesWithUniqueProfile || []).join(', '),
          },
          viewOptions: {
            extraButtons: [
              {
                labelPath: 'profile-manager.query.block-confirm-forward-to-view',
                click: () => this.showLinkedUsers(profile, true),
              },
            ],
            buttonsClasses: 's-btn-no-width s-btn-2x-side-padding',
            size: 'lg',
          },
        });
      }
      return this.$confirmMessage({ code: 'MC013' });
    },
    getLinkTypeMod(profileLinkType) {
      switch (profileLinkType) {
        case LinkType.ADMIN:
          return 'admin';
        case LinkType.ASSOCIATED:
          return 'associated';
        default:
          throw new Error(`Unknown link type: "${profileLinkType}".`);
      }
    },
    getIconForLink(profileLinkType) {
      switch (profileLinkType) {
        case LinkType.ADMIN:
          return 'cog';
        case LinkType.ASSOCIATED:
          return 'user-circle';
        default:
          throw new Error(`Unknown link type: "${profileLinkType}".`);
      }
    },
    filterLinkTypes(profileLinkTypes) {
      const isProfileAdmin = profileLinkTypes
        .filter(profileLinkType => profileLinkType === LinkType.ADMIN).length;
      return isProfileAdmin ? [LinkType.ADMIN]
        : profileLinkTypes.filter(profileLinkType => profileLinkType !== LinkType.CREATOR);
    },
    refreshItems(withDelay) {
      this.loading = true;
      this.items = [];

      const execRequest = withDelay ? this.doDelayedRequest : this.doRequest;

      return execRequest(this.getRequestFilter());
    },
    doRequest(requestFilter) {
      return Service
        .getProfiles(requestFilter)
        .then(this.getProjectRequestResult());
    },
    getRequestFilter() {
      const sort = {};
      sort[this.sortBy.field] = this.sortBy.desc ? 'desc' : 'asc';
      return {
        filter: {
          name: this.searchTerm,
        },
        page: this.currentPage,
        pageSize: this.perPage,
        sort,
      };
    },
    getProjectRequestResult() {
      return (paged) => {
        this.loading = false;
        this.totalItems = paged.total;
        this.items = paged.data;
      };
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.profile-list
  &__header
    display: flex
    flex-direction: column
    padding: 10px
    button
      margin-top: 1em
      width: 100%
  &__link-badge
    &_admin
      background-color: var(--color-extend-blue)
    &_associated
      background-color: var(--color-extend-deeporange)
    &_admin,
    &_associated
      padding: 5px
  ::v-deep &__cell
    &-link
      width: 5%
    &-name
      min-width: 100px
      width: 20%
    &-description
      max-width: 200px
    &-status
      width: 10%
    &-actions
      min-width: 245px
      width: 20%
  +media-min-sm()
    &__header
      button
        width: auto
  +media-min-md()
  +media-min-lg()
    &__header
      flex-direction: row
      justify-content: space-between
      button
        margin-top: 0
        + button
          margin-left: 1em
    &__search-input
      min-width: 350px
</style>
